import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconEdit, IconView } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';

interface BudgetRow {
  id: number;
  status: 'draft' | 'approved';
  financial_year: { id: number; name: string; start_date: string; end_date: string };
  lines_count: number;
  lines_sum_amount: number | null;
}

export function Budgets() {
  const { can } = useAuth();
  const { settings } = useConfig();
  const [budgets, setBudgets] = useState<BudgetRow[] | null>(null);
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const canCreate = can('budgets.create');

  useEffect(() => {
    api.get<BudgetRow[]>('/budgets').then((res) => setBudgets(res.data));
  }, []);

  return (
    <>
      <header className="page-header">
        <h1>Budgets</h1>
        {canCreate && (
          <Link className="btn btn-primary" to="/budgets/new">
            + New budget
          </Link>
        )}
      </header>
      <p className="muted">One budget per financial year, prepared as a set of cost-centre / GL account lines, then approved.</p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Financial Year</th>
                <th>Lines</th>
                <th className="right">Total Budget</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {budgets?.map((b) => (
                <tr key={b.id}>
                  <td>
                    <strong>{b.financial_year.name}</strong>
                  </td>
                  <td>{b.lines_count}</td>
                  <td className="right">{fmtMoney(b.lines_sum_amount ?? 0)}</td>
                  <td>
                    <span className={`badge ${b.status === 'approved' ? 'badge-active' : 'badge-neutral'}`}>
                      {b.status === 'approved' ? 'Approved' : 'Draft'}
                    </span>
                  </td>
                  <td className="right">
                    <IconButton icon={IconView} label="View" to={`/budgets/${b.id}`} />
                    {canCreate && b.status === 'draft' && (
                      <IconButton icon={IconEdit} label="Edit" to={`/budgets/${b.id}/edit`} />
                    )}
                  </td>
                </tr>
              ))}
              {budgets?.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted center">
                    No budgets yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
